import { Navigate, Route, Routes } from 'react-router';

import LoadingPage from '@/components/common/LoadingPage';
import AssistantRoutes from '@/features/assistant/AssistantRoutes';
import ClaimsRoutes from '@/features/claims/routes';
import ConceptReplyPage from '@/features/compose/ConceptReplyPage';
import DocumentsRoutes from '@/features/documents/routes';
import InvoicesRoutes from '@/features/invoices/InvoicesRoutes';
import LoginRoutes from '@/features/login/LoginRoutes';
import NotesRoutes from '@/features/notes/routes';
import OnesuranceRoutes from '@/features/onesurance/routes';
import PartyRoutes from '@/features/party/PartyRoutes';
import PoliciesRoutes from '@/features/policies/PoliciesRoutes';
import PolicyPackagesRoutes from '@/features/policies/PolicyPackagesRoutes';
import RiskObjectsRoutes from '@/features/risk-objects/RiskObjectsRoutes';
import Settings from '@/features/settings/Settings';
import useAuth from '@/hooks/useAuth';
import useOutlookConnectionError from '@/hooks/useOutlookConnectionError';
import useOutlookReminder from '@/hooks/useOutlookReminder';
import usePinReminder from '@/hooks/usePinReminder';
import usePosthogIdentify from '@/hooks/usePosthogIdentify';
import useSilentRenew from '@/hooks/useSilentRenew';
import useTheme from '@/hooks/useTheme';
import BlankLayout from '@/layouts/BlankLayout';
import EmailLayout from '@/layouts/EmailLayout';
import MainLayout from '@/layouts/MainLayout';
import paths from '@/lib/paths';

import PageNotFound from './PageNotFound';

const UnauthenticatedRoutes = () => {
  return (
    <Routes>
      <Route path="/login/*" element={<LoginRoutes />} />
      <Route
        path="*"
        element={
          <Navigate
            to={{
              pathname: paths.login.index,
              search: window.location.search,
            }}
            replace
          />
        }
      />
    </Routes>
  );
};

const AuthenticatedRoutes = () => {
  return (
    <Routes>
      <Route element={<EmailLayout />}>
        <Route element={<MainLayout />}>
          <Route
            index
            element={
              <Navigate
                to={{
                  pathname: paths.assistant,
                  search: window.location.search,
                }}
                replace
              />
            }
          />
          <Route path="/assistant/*" element={<AssistantRoutes />} />
          <Route path="/party/*" element={<PartyRoutes />} />
        </Route>

        <Route element={<BlankLayout />}>
          <Route path="/onesurance/*" element={<OnesuranceRoutes />} />
          <Route path="/policies/*" element={<PoliciesRoutes />} />
          <Route path="/policy-packages/*" element={<PolicyPackagesRoutes />} />
          <Route path="/risk-objects/*" element={<RiskObjectsRoutes />} />
          <Route path="/invoices/*" element={<InvoicesRoutes />} />
          <Route path="/claims/*" element={<ClaimsRoutes />} />
          <Route path="/documents/*" element={<DocumentsRoutes />} />
          <Route path="/notes/*" element={<NotesRoutes />} />
          <Route path="/settings" element={<Settings />} />
          <Route path={paths.conceptReply} element={<ConceptReplyPage />} />
        </Route>
      </Route>

      <Route path="/login/*" element={<LoginRoutes />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

export default function AppRouter() {
  const { isAuthenticated, isAuthLoading } = useAuth();
  useSilentRenew();
  useTheme();
  usePosthogIdentify();
  usePinReminder();
  useOutlookReminder();
  useOutlookConnectionError();

  if (isAuthLoading) return <LoadingPage />;

  if (!isAuthenticated) return <UnauthenticatedRoutes />;

  return <AuthenticatedRoutes />;
}
